/**
 * P4-N5B — SLICE C1 — Shared Presentation Catalog / Adapter (R1)
 *
 * Frontend-only canonical presentation catalog. Zero backend edits, zero
 * DB writes. This module is the SINGLE source of truth for user-facing
 * Network → Country semantics across Admin + Portal surfaces, but it does
 * NOT wire itself into any UI in this slice — consuming slices (C2–C7) do.
 *
 * Authoritative grounding:
 * - RCA R2 §5, §6, §7, §10, §11  — canonical UX model + dataset matrices
 *   `/app/rca_reports/p4_n5b_universal_network_country_semantic_symmetry_rca_r2/`
 * - USA Supplier Provenance RCA R1 §2, §3, §10, §13
 *   `/app/rca_reports/p4_n5b_usa_supplier_provenance_ta_petro_disambiguation_rca_r1/`
 *
 * ─── HARD INVARIANTS (enforced by tests T1–T24) ──────────────────────────
 * 1. Canonical visible providers are exactly: Esso · Pipeline · Petro-Pass.
 *    TA / TA-Petro NEVER appears in the visible catalog — its records
 *    (Esso-USA import identity) are surfaced under Esso + USA via the
 *    backend's existing `esso → {esso, ta_petro}` widening rule.
 * 2. Wire values emitted by this adapter are exclusively:
 *      network ∈ { "esso", "pipeline", "petro_pass" }
 *      country ∈ { "CA", "US", "" }
 *    NEVER: "ta", "ta_petro", "petro_pass_ca", "petro_pass_us",
 *           "esso_ca", "esso_us", "canada", "usa", "both", "all".
 * 3. Dataset capability matrices are DISTINCT — a transaction surface
 *    MUST NOT infer Esso-USA price-list capability from Esso-USA
 *    transaction capability (Esso price-lists are CA-only in production).
 * 4. Country label "All" (never "Both"). Empty-string wire ("") means
 *    "no country filter", identical to legacy backend semantics.
 * 5. Currency / UOM are DISPLAY-ONLY derivations from country. Zero
 *    financial recomputation. Never used to gate a wire call.
 * 6. This adapter performs NO backend-widening. It emits the geographic
 *    (network, country) pair verbatim; backend widening (12 call sites,
 *    RCA R2 §2) is the authority for the `esso → {esso, ta_petro}`
 *    union at query time.
 */

// ─── Canonical visible provider catalog ──────────────────────────────────
export const CANONICAL_NETWORKS = ["esso", "pipeline", "petro_pass"];

export const NETWORK_LABELS = {
  esso: "Esso",
  pipeline: "Pipeline",
  petro_pass: "Petro-Pass",
};

// ─── Canonical country catalog (order: All → Canada → USA) ───────────────
export const COUNTRY_ORDER = ["", "CA", "US"];

export const COUNTRY_LABELS = {
  "": "All",
  CA: "Canada",
  US: "USA",
};

export const ALL_COUNTRY_LABEL = "All"; // NEVER "Both"

// ─── Dataset capability matrices (RCA R2 §5, §6) ─────────────────────────
// These matrices are DISTINCT by dataset. Do NOT collapse them.
export const TRANSACTION_CAPABILITY = Object.freeze({
  esso: ["CA", "US"],
  pipeline: ["CA"],
  petro_pass: ["CA", "US"],
});

export const PRICE_LIST_CAPABILITY = Object.freeze({
  esso: ["CA"],
  pipeline: ["CA"],
  petro_pass: ["CA", "US"],
});

export const DATASETS = Object.freeze({
  transactions: TRANSACTION_CAPABILITY,
  price_lists: PRICE_LIST_CAPABILITY,
});

/**
 * Return the ordered list of visible provider keys for a dataset, filtered
 * to those with at least one supported country. Order matches
 * CANONICAL_NETWORKS. Always frontend-only.
 */
export function getVisibleNetworks(dataset) {
  const matrix = DATASETS[dataset];
  if (!matrix) return [];
  return CANONICAL_NETWORKS.filter(
    (n) => Array.isArray(matrix[n]) && matrix[n].length > 0
  );
}

/**
 * Return the ordered list of visible country wire values for a given
 * (dataset, network). Includes the empty-string "All" sentinel iff the
 * network supports more than one country in that dataset. For a
 * single-country network (e.g. Esso price-lists = CA only) the list
 * contains ONLY that country — no "All" sentinel, no cross-dataset
 * inference.
 */
export function getVisibleCountries(dataset, network) {
  const matrix = DATASETS[dataset];
  if (!matrix) return [];
  const supported = matrix[network];
  if (!Array.isArray(supported) || supported.length === 0) return [];
  if (supported.length === 1) return supported.slice();
  return ["", ...supported];
}

/**
 * Identity wire mapping. Emits ONLY canonical geographic pairs. Never
 * emits legacy compound wire values (ta, ta_petro, petro_pass_us, etc.).
 * Backend widening rule (RCA R2 §2, 12 call sites) is the authority for
 * `esso → {esso, ta_petro}`; this adapter must NOT duplicate it.
 *
 *  (esso,       CA)  → { network: "esso",       country: "CA" }
 *  (esso,       US)  → { network: "esso",       country: "US" }
 *  (esso,       "")  → { network: "esso" }
 *  (pipeline,   CA)  → { network: "pipeline",   country: "CA" }
 *  (petro_pass, CA)  → { network: "petro_pass", country: "CA" }
 *  (petro_pass, US)  → { network: "petro_pass", country: "US" }
 *  ("",         CA)  → { country: "CA" }
 *  ("",         "")  → { }
 */
export function toWireParams({ network, country } = {}) {
  const n = String(network || "").toLowerCase();
  const c = String(country || "").toUpperCase();
  const out = {};
  if (n && CANONICAL_NETWORKS.includes(n)) out.network = n;
  if (c === "CA" || c === "US") out.country = c;
  return out;
}

/** True iff `(network, country)` is supported by the dataset capability
 *  matrix (before any customer-authorization intersection). */
export function isNetworkCountryValid(dataset, network, country) {
  const matrix = DATASETS[dataset];
  if (!matrix) return false;
  const supported = matrix[network];
  if (!Array.isArray(supported)) return false;
  if (country === "" || country == null) return supported.length > 0;
  return supported.includes(country);
}

/** Currency / UOM derived from country. DISPLAY-ONLY. */
export function getCurrencyUom(country) {
  return String(country || "").toUpperCase() === "US"
    ? { currency: "USD", uom: "GAL" }
    : { currency: "CAD", uom: "L" };
}

/**
 * Customer-authorization intersection helper.
 *
 * MIRRORS EXISTING BACKEND SEMANTICS VERBATIM — does not invent or widen
 * business rules (operator constraint #10). Returns the ordered list of
 * `{ network, country }` pairs for the given dataset that are BOTH
 * (a) capability-supported AND (b) authorized for the customer.
 *
 * Authorization rules (mirror `resolve_authorized_price_report_lanes` +
 * `is_customer_lane_authorized` in `backend/routes/customers.py`):
 *
 *   dataset = "price_lists":
 *     (esso,       CA)  → customer.price_report_networks includes "esso"
 *     (pipeline,   CA)  → customer.price_report_networks includes "pipeline"
 *     (petro_pass, CA)  → customer.authorized_lanes contains {petro_pass, CA}
 *     (petro_pass, US)  → customer.authorized_lanes contains {petro_pass, US}
 *
 *   dataset = "transactions":
 *     Per RCA R2 §7 — transactions has no explicit per-lane authorization;
 *     historical convention is "any customer can query their own
 *     transactions on any provider they have transacted on." This helper
 *     therefore returns the dataset capability unchanged for the
 *     transactions dataset. Consuming slices that need a stricter gate
 *     must implement it themselves; this adapter refuses to invent one.
 */
export function getAuthorizedNetworkCountryPairs(dataset, customer) {
  const matrix = DATASETS[dataset];
  if (!matrix) return [];
  const pairs = [];
  for (const n of CANONICAL_NETWORKS) {
    const countries = matrix[n] || [];
    for (const c of countries) {
      pairs.push({ network: n, country: c });
    }
  }
  if (dataset === "transactions") return pairs;
  if (dataset !== "price_lists") return pairs;

  const priceReportNetworks = new Set(
    ((customer && customer.price_report_networks) || []).map((x) =>
      String(x || "").toLowerCase()
    )
  );
  const authorizedLanes = ((customer && customer.authorized_lanes) || [])
    .map((l) => ({
      network: String((l && l.network) || "").toLowerCase(),
      country: String((l && l.country) || "").toUpperCase(),
    }));

  return pairs.filter(({ network, country }) => {
    if (network === "esso" || network === "pipeline") {
      return country === "CA" && priceReportNetworks.has(network);
    }
    if (network === "petro_pass") {
      return authorizedLanes.some(
        (l) => l.network === "petro_pass" && l.country === country
      );
    }
    return false;
  });
}

/** Every canonical (network, country) pair the adapter can emit. Used by
 *  the C1 static tests to exhaustively prove wire safety. */
export const CANONICAL_WIRE_PAIRS = Object.freeze([
  { network: "esso", country: "CA" },
  { network: "esso", country: "US" },
  { network: "pipeline", country: "CA" },
  { network: "petro_pass", country: "CA" },
  { network: "petro_pass", country: "US" },
]);
